import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Loader2, DollarSign, Pencil, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { useLanguage } from '../context/LanguageContext';
import {
  getIncomeSourceTypes,
  getIncomeSources,
  createIncomeSource,
  updateIncomeSource,
  deleteIncomeSource,
} from '../lib/api';
import { toast } from 'sonner';

const IncomeEvolutionChart = lazy(() => import('./IncomeEvolutionChart'));

const EMPTY_FORM = {
  type: 'salary_main',
  custom_label: '',
  amount: '',
  frequency: 'monthly',
  date: '',
  start_date: '',
  end_date: '',
  notes: '',
  active: true,
};

export default function IncomeSourcesModal({ open, onOpenChange, onSaved }) {
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [typesCatalog, setTypesCatalog] = useState([]);
  const [data, setData] = useState({ sources: [], summary: null, effective: null });
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, sourcesRes] = await Promise.all([
        getIncomeSourceTypes(),
        getIncomeSources(),
      ]);
      setTypesCatalog(typesRes.data.types || []);
      setData(sourcesRes.data);
    } catch (e) {
      console.error(e);
      toast.error(t('failed_to_load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (open) {
      load();
      setEditingId(null);
      setForm(EMPTY_FORM);
    }
  }, [open, load]);

  const fmtCurrency = (v) =>
    new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(v || 0);

  const typeLabel = (t_id) => {
    const tdef = typesCatalog.find((x) => x.id === t_id);
    if (!tdef) return t_id;
    return `${tdef.emoji} ${tdef[`name_${language}`] || tdef.name_en}`;
  };

  const frequencyLabel = (freq) => ({
    monthly: t('income_frequency_monthly'),
    weekly: t('income_frequency_weekly'),
    daily: t('income_frequency_daily'),
    one_time: t('income_frequency_one_time'),
  }[freq] || freq);

  const startEdit = (src) => {
    setEditingId(src.source_id);
    setForm({
      type: src.type || 'salary_main',
      custom_label: src.custom_label || '',
      amount: String(src.amount ?? ''),
      frequency: src.frequency || 'monthly',
      date: src.date || '',
      start_date: src.start_date || '',
      end_date: src.end_date || '',
      notes: src.notes || '',
      active: src.active !== false,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amountNum = parseFloat(form.amount);
    if (isNaN(amountNum) || amountNum < 0) {
      toast.error(t('invalid_amount'));
      return;
    }
    if ((form.frequency === 'one_time' || form.frequency === 'daily') && !form.date) {
      toast.error(t('income_date') + ' ' + (language === 'fr' ? 'requise' : 'required'));
      return;
    }
    const payload = {
      type: form.type,
      custom_label: form.custom_label || null,
      amount: amountNum,
      frequency: form.frequency,
      date: (form.frequency === 'one_time' || form.frequency === 'daily') ? form.date : null,
      start_date: (form.frequency === 'monthly' || form.frequency === 'weekly') ? (form.start_date || null) : null,
      end_date: (form.frequency === 'monthly' || form.frequency === 'weekly') ? (form.end_date || null) : null,
      notes: form.notes || null,
      active: form.active,
    };
    setSubmitting(true);
    try {
      if (editingId) {
        await updateIncomeSource(editingId, payload);
        toast.success(t('income_source_updated'));
      } else {
        await createIncomeSource(payload);
        toast.success(t('income_source_added'));
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      await load();
      onSaved && onSaved();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || t('income_source_save_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (sourceId) => {
    if (!window.confirm(t('confirm_delete_income_source'))) return;
    try {
      await deleteIncomeSource(sourceId);
      toast.success(t('income_source_deleted'));
      await load();
      onSaved && onSaved();
    } catch (err) {
      toast.error(t('income_source_delete_failed'));
    }
  };

  const sources = data.sources || [];
  const summary = data.summary || {};
  const effective = data.effective || {};
  const isFamilyScope = effective.scope === 'family';

  const needsDateField = form.frequency === 'one_time' || form.frequency === 'daily';
  const isRecurring = form.frequency === 'monthly' || form.frequency === 'weekly';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="income-sources-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            {t('income_sources_title')}
          </DialogTitle>
          <DialogDescription>{t('income_sources_desc')}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary card */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-xl p-4 space-y-3 border border-emerald-200/50 dark:border-emerald-800/30">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{t('total_monthly_income')}</span>
                <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-testid="total-monthly-income">
                  {fmtCurrency(effective.total ?? summary.total)}
                </span>
              </div>
              {isFamilyScope && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{t('personal_income')}</span>
                  <span>{fmtCurrency(summary.total)}</span>
                </div>
              )}
              {summary.by_frequency && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-emerald-200/40 dark:border-emerald-800/30">
                  {['monthly', 'weekly', 'daily', 'one_time'].map((f) => (
                    <div key={f} className="text-center">
                      <p className="text-[10px] uppercase text-muted-foreground">{frequencyLabel(f)}</p>
                      <p className="text-xs font-semibold">{fmtCurrency(summary.by_frequency[f] || 0)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Evolution chart */}
            <Suspense fallback={null}>
              <IncomeEvolutionChart months={12} scope="personal" compact />
            </Suspense>

            {/* Add/Edit form */}
            <form onSubmit={handleSubmit} className="space-y-3 p-4 border rounded-xl bg-card" data-testid="income-source-form">
              <h4 className="font-medium text-sm flex items-center justify-between">
                <span>{editingId ? t('edit_income_source') : t('add_income_source')}</span>
                {editingId && (
                  <Button type="button" variant="ghost" size="sm" onClick={cancelEdit} data-testid="cancel-edit-btn">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">{t('income_type')}</label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="income-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {typesCatalog.map((tdef) => (
                        <SelectItem key={tdef.id} value={tdef.id}>
                          {tdef.emoji} {tdef[`name_${language}`] || tdef.name_en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">{t('income_frequency')}</label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger data-testid="income-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">{t('income_frequency_monthly')}</SelectItem>
                      <SelectItem value="weekly">{t('income_frequency_weekly')}</SelectItem>
                      <SelectItem value="daily">{t('income_frequency_daily')}</SelectItem>
                      <SelectItem value="one_time">{t('income_frequency_one_time')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">{t('income_amount')} (€)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    data-testid="income-amount-input"
                  />
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">{t('income_custom_label')}</label>
                  <Input
                    value={form.custom_label}
                    onChange={(e) => setForm({ ...form, custom_label: e.target.value })}
                    placeholder={language === 'fr' ? 'Ex: CAF, Mission UX...' : language === 'pt' ? 'Ex: CAF, Missão...' : 'Ex: CAF, UX gig...'}
                    data-testid="income-label-input"
                  />
                </div>

                {needsDateField && (
                  <div className="sm:col-span-2">
                    <label className="text-xs text-muted-foreground">{t('income_date')}</label>
                    <Input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      data-testid="income-date-input"
                    />
                  </div>
                )}

                {isRecurring && (
                  <>
                    <div>
                      <label className="text-xs text-muted-foreground">{t('income_start_date')}</label>
                      <Input
                        type="date"
                        value={form.start_date}
                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                        data-testid="income-start-date-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">{t('income_end_date')}</label>
                      <Input
                        type="date"
                        value={form.end_date}
                        onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                        data-testid="income-end-date-input"
                      />
                    </div>
                  </>
                )}
              </div>

              <div>
                <label className="text-xs text-muted-foreground">{t('income_notes')}</label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="income-notes-input"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                    data-testid="income-active-switch"
                  />
                  {form.active ? t('income_active') : t('income_inactive')}
                </label>
                <Button type="submit" disabled={submitting || !form.amount} data-testid="income-source-submit-btn">
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : editingId ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  {editingId ? t('save') : t('add_income_source')}
                </Button>
              </div>
            </form>

            {/* List */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm">{t('income_sources')}</h4>
              {sources.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-4">{t('no_income_sources')}</p>
              ) : (
                <AnimatePresence>
                  {sources.map((src) => (
                    <motion.div
                      key={src.source_id}
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className={`flex items-center justify-between bg-muted/30 hover:bg-muted/50 transition-colors rounded-lg p-3 ${!src.active ? 'opacity-50' : ''}`}
                      data-testid={`income-source-item-${src.source_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{typeLabel(src.type)}</span>
                          {src.custom_label && (
                            <span className="text-xs text-muted-foreground truncate">· {src.custom_label}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span>{frequencyLabel(src.frequency)}</span>
                          {src.date && <span>· {src.date}</span>}
                          {!src.active && <span className="text-amber-600">· {t('income_inactive')}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2 shrink-0">
                        <div className="text-right">
                          <p className="text-sm font-semibold">{fmtCurrency(src.amount)}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {t('monthly_contribution')}: {fmtCurrency(src.monthly_contribution)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEdit(src)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
                          data-testid={`edit-income-source-${src.source_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(src.source_id)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          data-testid={`delete-income-source-${src.source_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
